import type { BeforeNavigate } from '@sveltejs/kit';
import type { ParamsWithCategory } from '../../../params/poiCategory';
import type { SearchExtensionContextContent, SearchExtensionTouchpoint, TrackSearchExtensionParams } from './types';
import { browser } from '$app/environment';
import { pageIsCanonicalUrl } from '$lib/seo/utils';
import { track } from '$lib/services/tracking/googleAnalytics';
import { getBookingUrlTab } from '$lib/utils/getBookingUrlTab';
import { isNogarnish } from '$lib/utils/nogarnish';
import { get } from 'svelte/store';
import { isMobile } from '../mediaQuery/screenSize';
import { routeWithoutVanityParams } from '../urls/vanity';
import { SEARCH_EXTENSION_DESKTOP_BASE_URL, SEARCH_EXTENSION_KEY, SEARCH_EXTENSION_MAX_UPDATE, SEARCH_EXTENSION_MOBILE_BASE_URL, SEARCH_EXTENSION_TOUCHPOINT_KEY } from './constants';
import { searchExtensionContext } from './contextStore';
import { searchExtensionTouchpoint } from './touchpointStore';

export function setSearchExtension() {
	if (browser) {
		sessionStorage.setItem(SEARCH_EXTENSION_KEY, 'true');
	}
}

function setSearchExtensionTouchpoint(touchpoint: string) {
	if (browser) {
		sessionStorage.setItem(SEARCH_EXTENSION_TOUCHPOINT_KEY, touchpoint);
	}
}

function getSearchExtensionTouchpoint() {
	if (browser) {
		return sessionStorage.getItem(SEARCH_EXTENSION_TOUCHPOINT_KEY);
	}
}

export function getSearchExtensionValue(key: string) {
	if (browser) {
		return sessionStorage.getItem(key);
	}
}

export function searchExtensionTriggerIsAvailable() {
	if (browser && isNogarnish()) {
		return false;
	}
	const value = getSearchExtensionValue(SEARCH_EXTENSION_KEY);
	return value !== 'true';
}

export function trackSearchExtension({
	touchpoint,
	navigation,
	hasContext,
	isUpdated,
}: TrackSearchExtensionParams) {
	const se_navigation_to = navigation?.to?.url.toString();
	const has_context = hasContext ? 'true' : 'false';
	const is_se_updated = isUpdated ? 'true' : 'false';

	return track({
		action: 'search_extension',
		payload: {
			touchpoint,
			se_navigation_to,
			has_context,
			is_se_updated,
		},
	});
}

export function getSearchExtensionBaseUrl() {
	return get(isMobile) ? SEARCH_EXTENSION_MOBILE_BASE_URL : SEARCH_EXTENSION_DESKTOP_BASE_URL;
}

function getSearchExtensionUrl(searchExtensionContextContent: SearchExtensionContextContent, touchpoint: SearchExtensionTouchpoint) {
	return getBookingUrlTab(searchExtensionContextContent, touchpoint);
}

export async function handleSearchExtensionOpening(navigation: BeforeNavigate,	searchExtensionContextContent: SearchExtensionContextContent) {
	if (browser && navigation.to) {
		const comeFromCanonicalPage = navigation.from?.params && navigation.from?.url && pageIsCanonicalUrl(navigation.from?.url, navigation.from?.params as ParamsWithCategory);
		const newTabUrl = comeFromCanonicalPage ? routeWithoutVanityParams(navigation.to) : navigation.to?.url;

		const touchpoint = get(searchExtensionTouchpoint);
		const searchExtensionUrl = touchpoint && getSearchExtensionUrl(searchExtensionContextContent, touchpoint);
		const shouldOpenSE = !isNogarnish() && searchExtensionTriggerIsAvailable() && newTabUrl && searchExtensionUrl;

		if (shouldOpenSE) {
			navigation.cancel();
			setSearchExtension();
			setSearchExtensionTouchpoint(touchpoint);
			trackSearchExtension({
				touchpoint,
				navigation,
				hasContext: searchExtensionContext.hasContext(),
				isUpdated: false,
			});
			window.open(newTabUrl);
		}
	}
}

export async function updateSearchExtension(
	searchExtensionContextContent: SearchExtensionContextContent,
) {
	// Add a debugging door
	window.$searchExtensionContext = searchExtensionContextContent;

	if (isNogarnish()) {
		return; // disabled
	}

	const { updateCount } = searchExtensionContextContent;
	const searchExtensionWindow = !searchExtensionTriggerIsAvailable() && window.opener;
	const hasContext = searchExtensionContext.hasContext();

	if (searchExtensionWindow && updateCount <= SEARCH_EXTENSION_MAX_UPDATE && hasContext) {
		const searchExtensionUrl = getSearchExtensionUrl(searchExtensionContextContent, (getSearchExtensionTouchpoint() ?? undefined) as SearchExtensionTouchpoint);
		trackSearchExtension({
			hasContext,
			isUpdated: true,
		});

		// TODO : Improve this silent catch used for Firefox
		try {
			searchExtensionWindow.location.href = searchExtensionUrl;
		} catch {}
	}
}
